import AddIcon from '@mui/icons-material/Add';
import { Box, FormHelperText, Icon, Tooltip } from '@mui/material';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { Button } from 'global/components/Button';
import Comment from 'global/components/comment';
import Form from 'global/components/form';
import { Row } from 'global/components/row';
import { Text } from 'global/components/text';
import { first, get, size } from 'global/lib/lodash';
import { formatDateFromNow } from 'global/utils/formatDate';
import { useEffect, useState } from 'react';
import { userHasPermissions } from 'redux/actions/userLogin';
import { useUser } from '../../hooks/useUser';
import ConfirmModal from 'global/components/confirm';
import { useScreen } from 'global/styles';
import {
  CancelBtn,
  CommentContainerInner,
  CommentCount,
  CommentWrapTitle,
  CreateCommentContainer,
  PaddingTopBottom,
  ShowWrap,
  SubmitButtons,
} from './styles';

export const COMMENT_MAX_LENGTH = 2500;
export const COMMENT_AT_LIMIT_MESSAGE = `Your comment has reached the character limit of ${COMMENT_MAX_LENGTH} characters`;

export const CreateComment = (props) => {
  const { loading, onSubmitComment, onClose, commentState, setCommentState } = props;
  const { me } = useUser();
  const { isMobile } = useScreen();

  const [confirmModal, setConfirmModal] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetCommentState = () => {
    setCommentState({
      type: 'comments',
      attributes: {
        commentable_type: get(props, 'commentableType', ''),
        commentable_id: get(props, 'commentableId', ''),
        user_id: get(me, 'id'),
        body: '',
        images: [],
        meta: {},
      },
    });
  };

  useEffect(() => {
    if (!loading) setIsSubmitting(false);
  }, [loading]);

  const bodyAtLimit = size(get(commentState, 'attributes.body', '')) >= COMMENT_MAX_LENGTH;

  const handleSubmitComment = async () => {
    if (loading || isSubmitting) return;

    try {
      setIsSubmitting(true);
      await onSubmitComment(commentState);
    } finally {
      setIsSubmitting(false);
    }
  };

  const setFormState = (fs) => {
    const body = `${get(fs, 'body', get(commentState, 'attributes.body', ''))}`.slice(0, COMMENT_MAX_LENGTH);
    setCommentState((prev) => ({
      ...prev,
      attributes: { ...get(prev, 'attributes', {}), ...fs, body },
    }));
  };

  const isready = get(commentState, 'attributes.body') && !(get(commentState, 'attributes.images', []) || []).find((i) => i.loading);
  const hasBody = !!get(commentState, 'attributes.body');
  const hasImages = !!(get(commentState, 'attributes.images', []) || []).length;
  const hasContent = hasBody || hasImages;

  return (
    <>
      <CreateCommentContainer isMobile={isMobile}>
        <Form
          showAllScreens
          canSubmit={false}
          onFormState={setFormState}
          screens={[
            {
              title: 'Create Comment',
              inputs: [
                {
                  id: 'body',
                  type: 'TextField',
                  defaultValue: () => get(commentState, 'attributes.body', ''),
                  props: {
                    label: '',
                    multiline: true,
                    minRows: 5,
                    placeholder: 'Enter your comment here...',
                    inputProps: { maxLength: COMMENT_MAX_LENGTH },
                    error: (fs) => size(get(fs, 'body', '')) >= COMMENT_MAX_LENGTH,
                    helperText: (fs) =>
                      size(get(fs, 'body', '')) >= COMMENT_MAX_LENGTH ? COMMENT_AT_LIMIT_MESSAGE : undefined,
                  },
                },
                {
                  id: 'images',
                  type: 'DropArea',
                  defaultValue: () => get(commentState, 'attributes.images', []),
                  props: {
                    notAllowed: ['tag'],
                    uploadAllNew: true,
                    collection: 'comment-images',
                    shouldUpdateOnCaptionChange: true,
                  },
                },
              ],
            },
          ]}
        />
      </CreateCommentContainer>
      <SubmitButtons>
        <CancelBtn
          variant={'contained'}
          startIcon={<Icon>close</Icon>}
          onClick={() => {
            if (!hasContent) {
              resetCommentState();
              onClose();
              return;
            }
            setConfirmModal({
              title: 'Discard comment?',
              content: 'You have unsaved changes. Are you sure you want to discard this comment?',
              onOk: () => {
                resetCommentState();
                onClose();
                setConfirmModal();
              },
              onCancel: () => setConfirmModal(),
            });
          }}
        >
          Cancel
        </CancelBtn>

        <Tooltip title={bodyAtLimit ? COMMENT_AT_LIMIT_MESSAGE : props.commentDoneDisabled?.().text}>
          <span>
            <Button
              variant={'contained'}
              disabled={loading || isSubmitting || !hasContent || (!isready && props.commentDoneDisabled?.().disabled)}
              color="secondary"
              startIcon={<Icon>check</Icon>}
              onClick={handleSubmitComment}
              loading={loading}
            >
              Done
            </Button>
          </span>
        </Tooltip>
      </SubmitButtons>
      <ConfirmModal {...confirmModal} />
    </>
  );
};

const CommentContainer = (props) => {
  const { loading, comments, showCreateComment, onShowCreateComment, labelText, helperText } = props;
  const [currentCommentTab, setCurrentCommentTab] = useState('comment');
  const uhook = useUser();
  const { me } = uhook;
  const hasComments = get(comments, 'data.length', 0) > 0;
  const commentDates = () => {
    return `last updated ${formatDateFromNow(get(first(comments), 'createdAt', new Date()))}`;
  };
  const showTitle = get(props, 'showTitle', true);
  const canCreateComment = get(props, 'canCreateComment', true);

  useEffect(() => {
    if (showCreateComment && document.getElementById('create-comment')) {
      document.getElementById('create-comment').scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  }, [showCreateComment]);

  const handleCommentDeleted = (id) => {
    comments.update();
  };

  return (
    <CommentContainerInner>
      {showTitle && (
        <CommentWrapTitle>
          <Text type="title_large">
            <strong>{labelText || 'Activity'}</strong>
          </Text>
          {hasComments && <CommentCount>{commentDates()}</CommentCount>}
        </CommentWrapTitle>
      )}
      {helperText && <FormHelperText>{helperText}</FormHelperText>}
      {hasComments && (
        <>
          <ShowWrap>
            <Row>
              <Text>Show: </Text>
              <ToggleButtonGroup color="secondary" size="small" value={currentCommentTab} onChange={(e, v) => setCurrentCommentTab(v)} exclusive={true}>
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="comment">Comments</ToggleButton>
                <ToggleButton value="history">History</ToggleButton>
              </ToggleButtonGroup>
            </Row>
          </ShowWrap>
          {comments.data?.map((c) => (
            <Comment key={c.id} {...c} user={me} isAdmin={userHasPermissions(['manageAccount'])} onDeleted={() => handleCommentDeleted(c.id)} />
          ))}
        </>
      )}
      {canCreateComment && <PaddingTopBottom id="create-comment">
        {showCreateComment ? (
          <CreateComment
            loading={loading}
            onClose={() => {
              onShowCreateComment(false);
            }}
            {...props}
          />
        ) : (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end"
            }}>
            <Button
              startIcon={<AddIcon />}
              onClick={() => {
                onShowCreateComment(true);
              }}
            >
              Comment
            </Button>
          </Box>
        )}
      </PaddingTopBottom>}
    </CommentContainerInner>
  );
};

export default CommentContainer;
